<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { deleteAdminAction } from '@/api/functions/access/delete-admin-action'
import { getAdminsAction } from '@/api/functions/access/get-admins-action'
import { getRolesAction } from '@/api/functions/access/get-roles-action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import AccessOverview from './partials/AccessOverview.vue'
import CreateMemberModal from './partials/CreateMemberModal.vue'
import UpdateMemberModal from './partials/UpdateMemberModal.vue'

const showDeleteModal = ref(false)
const showUpdateModal = ref(false)
const showCreateModal = ref(false)

const selectedMember = ref()
const route = useRoute()
const toast = useToast()

const currentPage = ref(Number(route.query.page) || 1)

const tabs = [
  { title: 'اعضا', link: '/access' },
  { title: 'نقش‌ها', link: '/access/roles' },
  { title: 'مجوزها', link: '/access/permissions' },
]

const { data: adminsData, refetch } = useQueryApi({
  queryKey: computed(() => ['admins', currentPage.value]),
  queryFn: () => getAdminsAction(currentPage.value),
  // keepPreviousData: true,

  staleTime: 1000 * 60 * 3,
})

const { data: rolesData } = useQueryApi({
  queryKey: computed(() => ['roles', currentPage.value]),
  queryFn: () => getRolesAction(currentPage.value),
  // keepPreviousData: true,

  staleTime: 1000 * 60 * 3,
})

// --- computed for roles options for Select ---
const rolesOptions = computed(() => {
  const roles = rolesData.value?.data || []
  console.log(rolesData.value?.data)
  return roles.map((r) => ({
    label: r.name,
    value: r.id,
  }))
})

const members = computed(() => {
  const rows = adminsData.value?.data || []

  return rows.map((m) => {
    const isSuperAdmin = m.roles?.some((r) => r.superAdmin)

    return {
      fullName: m.fullName,
      email: m.email,
      status: m.status === 'ACTIVATE' ? 'فعال' : 'غیرفعال',
      roleName: m.roles?.map((r) => r.name).join('، ') || '-',
      permissions: isSuperAdmin
        ? 'تمام مجوزها'
        : m.roles?.flatMap((r) => r.permissions?.map((p) => p.label)).join('، ') || '-',
      raw: m,
    }
  })
})

const pagination = computed(() => adminsData.value?.pagination)
const totalItems = computed(() => (pagination.value?.total ? Number(pagination.value.total) : 0))

const columns = [
  { key: 'fullName', label: 'نام و نام خانوادگی', class: 'col-span-2 text-sm' },
  { key: 'email', label: 'ایمیل', class: 'col-span-3 text-sm' },
  { key: 'roleName', label: 'نقش', class: 'col-span-1 text-[13px] text-wrap w-14' },
  { key: 'status', label: 'وضعیت', class: 'col-span-1 text-sm' },
  { key: 'permissions', label: 'مجوزها', class: 'col-span-2 text-[12px]' },
]

const actions = [
  { label: 'ویرایش عضو', handlerName: 'editMember' },
  { label: 'حذف کاربر', handlerName: 'deleteMember' },
]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onEditMember = (member: any) => {
  selectedMember.value = member.raw
  showUpdateModal.value = true
}

// DELETE  MEMBER MUTATION
const deleteMutation = useMutationApi({
  mutationFn: (id: number) => deleteAdminAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'عضو با موفقیت حذف شد')
    refetch() // رفرش لیست اعضا
    showDeleteModal.value = false
    selectedMember.value = null
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onDeleteMember = (member: any) => {
  selectedMember.value = member.raw
  showDeleteModal.value = true
}

const handleDeleteMember = () => {
  if (!selectedMember.value?.id) {
    toast.error('عضوی برای حذف انتخاب نشده است', 'خطا')
    return
  }
  deleteMutation.mutate(selectedMember.value.id)
}

const goToPage = (page: any) => {
  currentPage.value = page
  refetch()
}
</script>

<template>
    <div class="w-full mb-12">
<!-- <MultiSelect test="123" /> -->

        <ConfirmModal v-model="showDeleteModal" title="حذف کاربر" :message="'آیا از حذف این کاربر اطمینان دارید؟'"
            confirm-text="بله حذف شود" cancel-text="بازگشت" @confirm="handleDeleteMember"
            @cancel="showDeleteModal = false" />


        <UpdateMemberModal v-model="showUpdateModal" :selectedMember="selectedMember" :rolesOptions="rolesOptions"
            @confirm="refetch" @cancel="showUpdateModal = false" />

        <CreateMemberModal v-model="showCreateModal" :rolesOptions="rolesOptions" @confirm="refetch"
            @cancel="showCreateModal = false" />

        <AccessOverview :tabs="tabs">
            <template #actions>
                <div class="flex items-center gap-2 pb-1">
                    <Button :isAdmin="true" @click="showCreateModal = true"
                        class="px-3! rounded-lg! h-8! py-0 text-[13px] font-medium">
                        افزودن عضو جدید
                    </Button>
                </div>
            </template>
        </AccessOverview>

        <div class="mt-20 lg:mt-7">
            <Table :columns="columns" :rows="members" :actions="actions" @primaryAction="onEditMember"
                @secondaryAction="onDeleteMember" />
        </div>

        <Pagination v-if="totalItems > 0" v-model:currentPage="currentPage" :itemsPerPage="pagination?.perPage"
            :totalItems="totalItems" :isAdmin="true" @update:currentPage="goToPage" />

    </div>
</template>
